import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Bell } from "lucide-react";
import { api, errMsg } from "@/lib/api";
import { Loader } from "@/components/brand";
import { Button } from "@/components/ui/button";
import { Switch } from "@/components/ui/switch";

const FREQ = [["instant", "Instantly", "One email per update, as it happens"], ["daily", "Daily summary", "One email each morning"], ["weekly", "Weekly summary", "One email on your chosen day"], ["off", "Off", "In-app notifications only"]];
const CATS = { documents: ["Documents", "Review requests, approvals, assignments"], training: ["Training", "New courses assigned to you"], permits: ["Permits to work", "Updates on permits you raised"], incidents: ["Incidents", "New reports, investigations and actions assigned to you"], reminders: ["Reminders", "Review dates, training expiry, overdue items"], billing: ["Billing", "Payments and subscription changes"], general: ["General", "Welcome and other account updates"] };
const DAYS = ["Monday", "Tuesday", "Wednesday", "Thursday", "Friday", "Saturday", "Sunday"];

export default function NotificationSettings() {
  const [p, setP] = useState(null);
  useEffect(() => {
    api.get("/me/notification-prefs").then((r) => setP(r.data));
  }, []);
  if (!p) return <Loader />;
  const save = async () => {
    try {
      await api.put("/me/notification-prefs", { frequency: p.frequency, weekly_day: p.weekly_day, categories: p.categories });
      toast.success("Notification preferences saved");
    } catch (e) {
      toast.error(errMsg(e));
    }
  };
  return (
    <div className="mx-auto max-w-3xl space-y-6" data-testid="notification-settings">
      <div><p className="eyebrow">Your account</p><h1 className="mt-2 text-3xl font-bold">Email notifications</h1>
        <p className="text-sm text-muted-foreground">You'll always see every update in the bell menu. Choose what reaches your inbox, and how often.</p></div>
      <section className="rounded-2xl border bg-card p-6">
        <h2 className="font-semibold">How often</h2>
        <div className="mt-4 grid gap-3 sm:grid-cols-2">{FREQ.map(([v, l, d]) => (
          <button key={v} onClick={() => setP({ ...p, frequency: v })} data-testid={`freq-${v}`} className={`rounded-xl border p-4 text-left transition-colors ${p.frequency === v ? "border-health bg-health/10" : "hover:bg-accent"}`}>
            <p className="font-medium">{l}</p><p className="text-xs text-muted-foreground">{d}</p></button>
        ))}</div>
        {p.frequency === "weekly" && <div className="mt-4 flex items-center gap-3 text-sm"><span>Send on</span>
          <select className="rounded-lg border bg-background px-3 py-2" value={p.weekly_day} onChange={(e) => setP({ ...p, weekly_day: Number(e.target.value) })} data-testid="weekly-day">{DAYS.map((d, i) => <option key={d} value={i}>{d}</option>)}</select></div>}
        {["daily", "weekly"].includes(p.frequency) && <p className="mt-3 text-xs text-muted-foreground">Summaries are sent with the morning run (around 7am UK time).</p>}
      </section>
      <section className={`rounded-2xl border bg-card p-6 ${p.frequency === "off" ? "opacity-50" : ""}`}>
        <h2 className="font-semibold">What to email me about</h2>
        <ul className="mt-4 divide-y">{Object.entries(CATS).map(([k, [l, d]]) => (
          <li key={k} className="flex items-center justify-between py-3"><div><p className="text-sm font-medium">{l}</p><p className="text-xs text-muted-foreground">{d}</p></div>
            <Switch checked={p.categories[k] !== false} disabled={p.frequency === "off"} onCheckedChange={(v) => setP({ ...p, categories: { ...p.categories, [k]: v } })} data-testid={`cat-${k}`} /></li>
        ))}</ul>
      </section>
      <div className="flex items-start gap-3 rounded-xl border border-[#FFA000]/30 bg-[#FFA000]/10 p-4 text-sm"><Bell className="mt-0.5 h-4 w-4 shrink-0 text-[#B87300]" />
        Safety-critical messages are always emailed immediately: permit authorisation requests, permits authorised, returned, suspended or resumed. Security emails (password reset, verification, invitations) are always sent too.</div>
      <Button onClick={save} data-testid="save-notification-prefs">Save preferences</Button>
    </div>
  );
}
